<template lang="html">
  <component :is="tag">
    <component v-for="({ componentType, name, attrs, listeners }) in fieldAttrs"
      :is="componentType"
      :key="name"
      v-bind="{ ...attrs, name }"
      v-on="listeners"
    />
  </component>
</template>

<script>
import { defaultSchema as focusSchema } from '../../directives/focus';

const { prop: focusProp } = focusSchema;

const MODEL_EVENT = 'update';
const MODEL_PROP = 'model';

export default {
  inheritAttrs: false,

  model: {
    event: MODEL_EVENT,
    prop: MODEL_PROP
  },

  props: {
    autoFocusOnFirstField: {
      type: Boolean,
      default: true
    },

    /**
     * Common attributes to be passed to each field.
     */
    commonAttrs: {
      type: Object,
      default: () => ({})
    },

    /**
     * Whether the form is currently in read-only mode. The prop is automatically
     * forwarded to all child fields.
     * Defaults to false.
     */
    readonly: {
      type: Boolean,
      default: false
    },

    /**
     * Fields support the following properties:
     *  name: String (required)
     *  componentType: Component (required)
     *  visible: Boolean
     *  changeGuard: Function - invoked with the tentative new value and aborts
     *    emitting an update event if the function returns false.
     * as well as any other arbitrary attributes/props to be passed to the field.
     * NOTE: If field attribute/prop names conflict with the above properties, supply
     * via a nested "attrs" object.
     */
    fields: {
      type: Array,
      required: true
    },

    /**
     * Focus on the specified field by name.
     */
    focusOnField: String,

    /**
     * Specify the root element tag (e.g. "form"). Default is "div".
     */
    tag: {
      type: String,
      default: () => 'div'
    },

    /**
     * A model object containing the values for the provided fields.
     * Use with v-model for streamlined model synchronization.
     */
    [MODEL_PROP]: {
      type: Object,
      required: true
    }
  },

  data({ autoFocusOnFirstField }) {
    const focusedFieldIndexInitial = autoFocusOnFirstField ? 0 : -1;

    return {
      focusedFieldIndexInitial,
      focusedFieldIndex: focusedFieldIndexInitial
    };
  },

  computed: {
    fieldAttrs() {
      return this.fields
        // Conditionally render a field using the "visible" flag.
        .filter((f) => f.visible !== false)
        .map(({
          attrs = {}, changeGuard, componentType, name, visible, ...f
        }, i) => ({
          // The name of the field.
          name,
          // The field component type.
          componentType,
          listeners: {
            focus: () => { this.focusedFieldIndex = i; },
            blur: () => { this.focusedFieldIndex = -1; },
            // A change guard function may be specified, which is passed the new value and
            // only triggers the update event if the function returns true.
            [componentType.model?.event || 'input']: (val) => this.handleChange(name, val, changeGuard || (() => true))
          },
          // Attributes to pass to the field component.
          attrs: {
            readonly: this.readonly,
            ...this.commonAttrs,
            // Value is derived from the model using the field name.
            [componentType.model?.prop || 'value']: this[MODEL_PROP][name],
            // Set focus on the field using v-focus when appropriate.
            [componentType.focusSchema?.prop || focusProp]: this.focusedFieldIndex === i,
            ...f,
            ...attrs
          }
        }));
    }
  },

  methods: {
    handleChange(name, $event, changeGuard) {
      if (!changeGuard($event)) return;

      // Support v-model by assigning to a new object, even though it's less efficient
      // than updating individual properties.
      // Also ensures that dynamic properties are reactive.
      this.$emit(MODEL_EVENT, {
        ...this[MODEL_PROP],
        [name]: $event
      });
    }
  },

  watch: {
    autoFocusOnFirstField(val) {
      this.focusedFieldIndexInitial = val ? 0 : -1;
      if (val) this.focusedFieldIndex = 0;
    },

    focusedFieldIndex() {
      const focusedFieldName = this.fieldAttrs[this.focusedFieldIndex]?.name || '';
      if (this.focusOnField !== focusedFieldName) this.$emit('update:focusOnField', focusedFieldName);
    },

    focusOnField: {
      immediate: true,
      handler(value) {
        if (value) this.focusedFieldIndex = this.fieldAttrs.findIndex((f) => f.name === value);
      }
    },

    readonly(value) {
      // Reset focusedFieldIndex when the form is taken out of readonly mode
      if (!value) {
        this.focusedFieldIndex = this.focusedFieldIndexInitial;
      }
    }
  }
};
</script>
